import { dealerLabel } from "@/lib/naming";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { OrderSheetDialog } from "@/components/OrderSheetDialog";
import { RESERVATION_STATUS, human } from "@/lib/statusLabels";
import { toast } from "sonner";
import { Loader2, CalendarClock, Ship, Warehouse, ShoppingBag } from "lucide-react";

const STATUS_BADGE = {
  ACTIVE: "badge-available",
  PENDING_APPROVAL: "badge-reserved",
  DECLINED: "badge-sold",
  EXPIRED: "badge-neutral",
  CANCELLED: "badge-neutral",
  CONVERTED: "badge-available",
};

function Countdown({ expiresAt, id }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return <span className="text-xs text-status-sold">Expired</span>;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return (
    <span className={`font-mono text-xs ${h < 4 ? "text-status-sold" : "text-warmgrey"}`} data-testid={`countdown-${id}`}>
      {h}h {m}m remaining
    </span>
  );
}

export default function Reservations() {
  const [rows, setRows] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusId = params.get("focus");

  useEffect(() => {
    if (!rows || !focusId) return;
    document.getElementById(`reservation-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [rows, focusId]);

  const load = () => {
    api
      .get("/reservations")
      .then((res) => setRows(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(load, []);

  const handleCancel = async (id) => {
    try {
      await api.post(`/reservations/${id}/cancel`);
      toast.success("Reservation cancelled — stock released");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!rows) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="reservations-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  const active = rows.filter((r) => r.effective_status === "ACTIVE" || r.status === "PENDING_APPROVAL");
  const history = rows.filter((r) => !(r.effective_status === "ACTIVE" || r.status === "PENDING_APPROVAL"));

  const ReservationCard = ({ r }) => (
    <Card id={`reservation-${r.id}`} className={`border-border bg-white ${r.id === focusId ? "ring-2 ring-terracotta" : ""}`} data-testid={`reservation-card-${r.id}`}>
      <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
        <div className="flex items-start gap-4">
          {r.source === "FLORIDA" ? (
            <Warehouse className="mt-1 h-4 w-4 text-terracotta" />
          ) : (
            <Ship className="mt-1 h-4 w-4 text-terracotta" />
          )}
          <div>
            <p className="font-medium text-charcoal">{dealerLabel(r)}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {r.boxes} boxes · {r.sqft} sqft · {r.source === "FLORIDA" ? "Florida warehouse" : "In transit"}
              {r.project_name ? ` · Project: ${r.project_name}` : ""}
            </p>
            {r.effective_status === "ACTIVE" && <div className="mt-2"><Countdown expiresAt={r.expires_at} id={r.id} /></div>}
            {r.status === "PENDING_APPROVAL" && (
              <p className="mt-2 text-xs text-[#8a6435]">Repeat hold — awaiting Zelligio approval. Stock is not held yet.</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`badge-status ${STATUS_BADGE[r.effective_status] || "badge-neutral"}`} data-testid={`reservation-status-${r.id}`}>
            {human(RESERVATION_STATUS, r.effective_status)}
          </span>
          {r.effective_status === "ACTIVE" && (
            <>
              <Button
                size="sm"
                data-testid={`convert-reservation-${r.id}`}
                onClick={() => setConvertTarget(r)}
                className="bg-terracotta text-white hover:bg-terracotta-hover"
              >
                <ShoppingBag className="mr-1 h-3.5 w-3.5" /> Convert to order
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid={`cancel-reservation-${r.id}`}
                onClick={() => handleCancel(r.id)}
                className="border-limestone text-status-sold hover:bg-sand"
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="dealer-reservations-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Holds</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Reservations</h1>
      <p className="mt-3 text-sm text-warmgrey">
        First holds are confirmed automatically for 72 hours. Repeat holds for the same product and project require Zelligio approval.
      </p>

      {rows.length === 0 ? (
        <Card className="mt-12 border-border bg-white">
          <CardContent className="flex flex-col items-center p-16 text-center">
            <CalendarClock className="h-8 w-8 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground" data-testid="reservations-empty">No reservations yet. Reserve stock from the catalog.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mt-10 space-y-3">
            {active.map((r) => <ReservationCard key={r.id} r={r} />)}
          </div>
          {history.length > 0 && (
            <>
              <p className="mt-10 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">History</p>
              <div className="mt-4 space-y-3 opacity-80">
                {history.map((r) => <ReservationCard key={r.id} r={r} />)}
              </div>
            </>
          )}
        </>
      )}

      <OrderSheetDialog
        open={!!convertTarget}
        onOpenChange={(o) => !o && setConvertTarget(null)}
        reservation={convertTarget}
        onCreated={(order) => navigate(`/dealer/orders/${order.id}`)}
      />
    </div>
  );
}
